import { ChessTermType, TermAction, } from "@chesshotel/types";
import { detectHangingPieces, exchangeEvaluation, pieceValues, uci2move, } from "./util.js";
import { Chess } from "@chesshotel/chess";
import { detectThreat } from "./attacks.js";
import { isCastleMove } from "./kingSafety.js";
function detectInCheckMoveToSafety(fen, moveStr, checking) {
    const chess = new Chess(fen);
    const moveResult = chess.move(uci2move(moveStr));
    if (moveStr)
        if (moveResult.from == checking.to.square) {
            return {
                term: ChessTermType.IN_CHECK_MOVE_TO_SAFETY,
                mainPiece: checking.to,
                move: moveResult,
            };
        }
}
function detectInCheckBlock(fen, moveStr, checking) {
    const chess = new Chess(fen);
    const moveResult = chess.move(uci2move(moveStr));
    const blocksCheck = chess.doesMoveBlockThreat(moveResult.to, checking.from.square, checking.to.square);
    if (blocksCheck) {
        return {
            term: ChessTermType.IN_CHECK_BLOCK,
            mainPiece: chess.getBoardPiece(moveResult.to),
            attackedPiece: checking.to,
            involvedPiece: checking.from,
            move: moveResult,
        };
    }
}
function detectMoveToSafety(fen, moveStr, vulnerablePieces) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const moveResult = chess.move(uci2move(moveStr));
    const movedVulnerablePiece = vulnerablePieces.find(({ to }) => to.square === moveResult.from);
    const capturedAttackingPiece = vulnerablePieces.find(({ from }) => from.square === moveResult.to);
    if (movedVulnerablePiece && !capturedAttackingPiece) {
        const exchange = exchangeEvaluation(chess.fen(), moveResult.to);
        if (exchange.gain > 0) {
            return null;
        }
        const translationKeys = {
            piece: movedVulnerablePiece.to.type,
            color,
        };
        return {
            term: ChessTermType.MOVE_TO_SAFETY,
            mainPiece: movedVulnerablePiece.to,
            move: moveResult,
            translationKeys,
        };
    }
}
export function detectMoveToSafetyPositive(position, lastPosition) {
    const threats = lastPosition.threats;
    if (!threats) {
        return;
    }
    const moveToSafety = detectMoveToSafety(lastPosition.fen, position.move, threats);
    if (moveToSafety) {
        return {
            ...moveToSafety,
            termAction: TermAction.PERFORM,
        };
    }
}
export function detectAvoidCheckPositive(position, lastPosition) {
    const checking = lastPosition.inCheck;
    if (!checking) {
        return;
    }
    const moveKingToSafety = detectInCheckMoveToSafety(lastPosition.fen, position.move, checking);
    if (moveKingToSafety) {
        return {
            ...moveKingToSafety,
            termAction: TermAction.PERFORM,
        };
    }
    const block = detectInCheckBlock(lastPosition.fen, position.move, checking);
    if (block) {
        return {
            ...block,
            termAction: TermAction.PERFORM,
        };
    }
}
export function detectAvoidCheckNegative(position, lastPosition) {
    const checking = lastPosition.inCheck;
    if (!checking) {
        return;
    }
    const user = detectInCheckMoveToSafety(lastPosition.fen, position.move, checking);
    const best = detectInCheckMoveToSafety(lastPosition.fen, lastPosition.bestMove, checking);
    if (user && best) {
        return {
            ...best,
            termAction: TermAction.BETTER_WAY,
        };
    }
    const userBlock = detectInCheckBlock(lastPosition.fen, position.move, checking);
    const bestBlock = detectInCheckBlock(lastPosition.fen, lastPosition.bestMove, checking);
    if (userBlock && bestBlock) {
        return {
            ...bestBlock,
            termAction: TermAction.BETTER_WAY,
        };
    }
    if ((user && bestBlock) || (userBlock && best)) {
        const chessTerm = bestBlock || best;
        return {
            ...chessTerm,
            term: ChessTermType.IN_CHECK_ESCAPE,
            termAction: TermAction.BETTER_WAY,
        };
    }
}
export function detectMoveToSafetyNegative(position, lastPosition) {
    const threats = lastPosition.threats;
    if (!threats) {
        return;
    }
    const user = detectMoveToSafety(lastPosition.fen, position.move, threats);
    const best = detectMoveToSafety(lastPosition.fen, lastPosition.bestMove, threats);
    if (user && best && user.move.from == best.move.from) {
        return {
            ...best,
            termAction: TermAction.BETTER_WAY,
        };
    }
    if (!user && best) {
        return {
            ...best,
            termAction: TermAction.MISS,
        };
    }
}
function detectBlock(fen, moveStr, vulnerablePieces) {
    const chess = new Chess(fen);
    const moveResult = chess.move(uci2move(moveStr));
    const blocked = vulnerablePieces.find(({ from, to }) => {
        return chess.doesMoveBlockThreat(moveResult.to, from.square, to.square);
    });
    if (blocked) {
        return {
            term: ChessTermType.BLOCK,
            mainPiece: chess.getBoardPiece(moveResult.to),
            attackedPiece: blocked.to,
            involvedPiece: blocked.from,
            arrows: [
                { from: blocked.from.square, to: blocked.to.square },
                { from: moveResult.from, to: moveResult.to },
            ],
            highlightSquares: [moveResult.from, moveResult.to],
            move: moveResult,
        };
    }
}
function detectPositiveBlock(position, lastPosition) {
    const threats = lastPosition.threats;
    if (!threats) {
        return;
    }
    const block = detectBlock(lastPosition.fen, position.move, threats);
    if (block) {
        return {
            ...block,
            termAction: TermAction.PERFORM,
        };
    }
}
function detectNegativeBlock(position, lastPosition) {
    const threats = lastPosition.threats;
    if (!threats) {
        return;
    }
    const user = detectBlock(lastPosition.fen, position.move, threats);
    const best = detectBlock(lastPosition.fen, lastPosition.bestMove, threats);
    if (user && best) {
        return {
            ...best,
            termAction: TermAction.BETTER_WAY,
        };
    }
    if (!user && best) {
        return {
            ...best,
            termAction: TermAction.MISS,
        };
    }
}
function detectDefend(fen, moveStr, threats) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const moveObj = uci2move(moveStr);
    const isCastle = isCastleMove(fen, moveStr);
    if (isCastle) {
        return null;
    }
    if (threats.length == 1) {
        const threat = threats[0];
        const moveObj = uci2move(moveStr);
        if (moveObj.from == threat.to.square) {
            return null;
        }
    }
    const defence = [];
    threats.forEach((threat) => {
        const { to } = threat;
        const moveObj = uci2move(moveStr);
        const canDefendBefore = chess.canDefend(to.square, moveObj.from, color);
        chess.move(moveObj);
        const notDefendItself = moveObj.from != to.square;
        const canDefendAfter = chess.canDefend(to.square, moveObj.to, color);
        const defender = chess.getBoardPiece(moveObj.to);
        chess.undo();
        if (notDefendItself &&
            !canDefendBefore &&
            canDefendAfter) {
            defence.push({ threat, defender });
        }
    });
    if (defence.length == 0) {
        const pieces = chess.pieces(color);
        const moveObj = uci2move(moveStr);
        threats.forEach((threat) => {
            const piecesNotMoving = pieces.filter((piece) => piece.square != moveObj.from && piece.square != threat.to.square);
            const canDefendBefore = piecesNotMoving.map((piece) => ({
                piece,
                canDefend: chess.canDefend(threat.to.square, piece.square, color),
            }));
            chess.move(moveObj);
            const canDefendAfter = piecesNotMoving.map((piece) => ({
                piece,
                canDefend: chess.get(piece.square) &&
                    chess.get(threat.to.square) &&
                    chess.canDefend(threat.to.square, piece.square, color),
            }));
            chess.undo();
            for (let i = 0; i < canDefendBefore.length; i++) {
                const canDefendBeforeItem = canDefendBefore[i];
                const canDefendAfterItem = canDefendAfter[i];
                if (!canDefendBeforeItem.canDefend &&
                    canDefendAfterItem.canDefend &&
                    !isCastle) {
                    defence.push({
                        defender: canDefendBeforeItem.piece,
                        threat,
                    });
                }
            }
        });
    }
    const moveResult = chess.move(moveObj);
    if (defence.length == 0) {
        return;
    }
    const mainDefence = defence.sort((a, b) => pieceValues[b.threat.to.type] - pieceValues[a.threat.to.type])[0];
    const { threat, defender } = mainDefence;
    const defendedPiece = threat.to;
    const attackingPiece = threat.from;
    const translationKeys = { piece: defendedPiece.type, color };
    const arrows = [{ from: defender.square, to: threat.to.square }];
    return {
        term: ChessTermType.DEFEND,
        mainPiece: defender,
        move: moveResult,
        attackedPiece: defendedPiece,
        involvedPiece: attackingPiece,
        arrows,
        translationKeys,
    };
}
function detectPositiveDefend(position, lastPosition) {
    const threats = lastPosition.threats;
    if (!threats) {
        return;
    }
    const user = detectDefend(lastPosition.fen, position.move, threats);
    if (user) {
        return {
            ...user,
            termAction: TermAction.PERFORM,
        };
    }
}
function detectNegativeDefend(position, lastPosition) {
    const threats = lastPosition.threats;
    if (!threats) {
        return;
    }
    const moveToSafety = detectMoveToSafety(lastPosition.fen, position.move, threats);
    if (moveToSafety) {
        return null;
    }
    const user = detectDefend(lastPosition.fen, position.move, threats);
    const best = detectDefend(lastPosition.fen, lastPosition.bestMove, threats);
    if (user && best) {
        const { move, attackedPiece } = best;
        const arrows = [
            { from: move.from, to: move.to },
            {
                from: move.to,
                to: attackedPiece.square,
            },
        ];
        return {
            ...best,
            arrows,
            termAction: TermAction.BETTER_WAY,
        };
    }
    if (!user && best) {
        const { attackedPiece, involvedPiece } = best;
        const arrows = [{ from: involvedPiece.square, to: attackedPiece.square }];
        return {
            ...best,
            termAction: TermAction.MISS,
            arrows,
        };
    }
}
export function detectNegativeDefence(position, lastPosition) {
    const terms = [];
    const avoidCheck = detectAvoidCheckNegative(position, lastPosition);
    if (avoidCheck) {
        terms.push(avoidCheck);
        return terms;
    }
    const moveToSafety = detectMoveToSafetyNegative(position, lastPosition);
    if (moveToSafety)
        terms.push(moveToSafety);
    const block = detectNegativeBlock(position, lastPosition);
    if (block)
        terms.push(block);
    const defend = detectNegativeDefend(position, lastPosition);
    if (defend)
        terms.push(defend);
    const counterAttack = detectNegativeCounterAttack(position, lastPosition);
    if (counterAttack)
        terms.push(counterAttack);
    return terms;
}
export function detectPositiveDefence(position, lastPosition) {
    const avoidCheck = detectAvoidCheckPositive(position, lastPosition);
    if (avoidCheck) {
        return avoidCheck;
    }
    const moveToSafety = detectMoveToSafetyPositive(position, lastPosition);
    if (moveToSafety) {
        return moveToSafety;
    }
    const block = detectPositiveBlock(position, lastPosition);
    if (block)
        return block;
    const defend = detectPositiveDefend(position, lastPosition);
    if (defend)
        return defend;
    const counterAttack = detectPositiveCounterAttack(position, lastPosition);
    if (counterAttack)
        return counterAttack;
}
function detectNegativeCounterAttack(position, lastPosition) {
    const threats = lastPosition.threats;
    if (!threats) {
        return;
    }
    const user = detectCounterAttack(lastPosition.fen, position.move);
    const best = detectCounterAttack(lastPosition.fen, lastPosition.bestMove);
    if (user && best) {
        return {
            ...best,
            termAction: TermAction.BETTER_WAY,
        };
    }
    if (!user && best) {
        return {
            ...best,
            termAction: TermAction.MISS,
        };
    }
}
function detectPositiveCounterAttack(position, lastPosition) {
    const threats = lastPosition.threats;
    if (!threats) {
        return;
    }
    if (position.chessTerm) {
        return null;
    }
    const counterAttack = detectCounterAttack(lastPosition.fen, position.move);
    if (counterAttack) {
        return {
            ...counterAttack,
            termAction: TermAction.PERFORM,
        };
    }
}
export function detectCounterAttack(fen, moveStr) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const hangingPieces = detectHangingPieces(fen, color);
    if (hangingPieces.length == 0) {
        return null;
    }
    chess.move(uci2move(moveStr));
    const hangingPiecesAfter = detectHangingPieces(chess.fen(), color);
    if (hangingPiecesAfter.length == 0) {
        return null;
    }
    const counterAttack = detectThreat(fen, moveStr);
    if (!counterAttack) {
        return null;
    }
    return {
        ...counterAttack,
        term: ChessTermType.COUNTER_ATTACK,
    };
}
//# sourceMappingURL=defence.js.map